import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, Star, Check, ArrowRight, Globe } from "lucide-react";
import { absUrl } from "@/lib/api";
import { categoryPath } from "@/lib/paths";
import VerifiedBadge from "@/components/VerifiedBadge";
import FreeEnquiryForm from "@/components/FreeEnquiryForm";

const FOUNDING_PERKS = [
  "Receive matched RFQs from hotel buyers",
  "Product pages, gallery & brand profile",
  "Priority placement above free listings",
  "Founding Supplier badge + locked-in launch pricing",
];

function UpgradePrompt({ variant, testId }) {
  const owner = variant === "owner";
  return (
    <div className="border-2 border-[#B8860B]/40 bg-gradient-to-br from-[#FFFBEB] to-[#F0EFEA] p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#B8860B] tracking-overline mb-2"><Star className="w-4 h-4 fill-current" /> Founding Supplier</div>
      <div className="font-serif text-xl text-[#11211A]">
        {owner ? "Upgrade your free listing to Founding Supplier" : "This company hasn't upgraded yet"}
      </div>
      <p className="text-sm text-[#4A5550] mt-2 leading-relaxed">
        {owner
          ? "Your listing is live but buyers can't send you quote requests. Founding Suppliers appear above free listings and receive matched RFQs directly."
          : "Founding Suppliers get full profiles, product pages, matched RFQs and priority placement. If this is your company, claim the listing then upgrade."}
      </p>
      <ul className="mt-4 space-y-1.5 text-sm text-[#4A5550]">
        {FOUNDING_PERKS.map((p) => (
          <li key={p} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#264839] mt-0.5 shrink-0" />{p}</li>
        ))}
      </ul>
      <Link to="/pricing" className="btn-primary mt-5 inline-flex" data-testid={`${testId}-cta`}>
        {owner ? "Upgrade to Founding Supplier" : "See Founding Supplier plans"} <ArrowRight className="w-4 h-4" />
      </Link>
      {owner && (
        <p className="text-xs text-[#7D8C85] mt-3">
          Not ready yet? <Link to="/dashboard" className="underline hover:text-[#264839]" data-testid="free-owner-edit-link">Edit your listing or upload your logo</Link> — free.
        </p>
      )}
    </div>
  );
}

export default function FreeListingDetail({ item, user, onClaim }) {
  const company = item.company || item.advertiser?.company || item.advertiser_name || item.title;
  const isOwner = !!user && user.id === item.advertiser_id;
  const categoryNames = [item.category?.name].filter(Boolean);
  const region = [item.city, item.country || item.country_code].filter(Boolean).join(", ");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="listing-detail">
      <div className="text-sm text-[#7D8C85] mb-6">
        <Link to="/categories" className="hover:text-[#264839]">Categories</Link>
        {item.category && <> / <Link to={categoryPath(item.category)} className="hover:text-[#264839]">{item.category.name}</Link></>}
      </div>

      <div className="bg-white border border-dashed border-[#C4CFC9] p-6 md:p-8 mb-8">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="w-20 h-20 bg-[#F0EFEA] flex items-center justify-center flex-shrink-0 border border-[#E8ECE9]">
            {item.logo_url ? <img src={absUrl(item.logo_url)} alt="logo" className="w-full h-full object-contain" /> : <Building2 className="w-8 h-8 text-[#7D8C85]" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7D8C85] bg-[#E8ECE9] px-2 py-0.5" data-testid="free-listing-badge-detail">Free Listing</span>
              {item.is_unclaimed && <span className="text-[10px] uppercase tracking-[0.2em] text-[#B35C4A]" data-testid="unclaimed-badge-detail">Unclaimed</span>}
              {isOwner && !item.is_unclaimed && <span className="text-[10px] uppercase tracking-[0.2em] text-[#264839] bg-[#E8ECE9] px-2 py-0.5" data-testid="owner-badge-detail">You manage this listing</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight" data-testid="free-company-name">{company}</h1>
              {item.is_verified && <VerifiedBadge />}
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#4A5550]">
              {region && <span className="inline-flex items-center gap-1" data-testid="free-country"><MapPin className="w-4 h-4 text-[#264839]" />{region}</span>}
              {categoryNames.map((c) => <span key={c} className="text-xs bg-[#E8ECE9] text-[#264839] px-3 py-1" data-testid="free-category-chip">{c}</span>)}
            </div>
            {item.website_domain && (
              <div className="mt-2" data-testid="free-website-block">
                <div className="inline-flex items-center gap-1.5 text-sm text-[#4A5550] select-text" title="Website shown as text only on free listings" data-testid="free-website-domain">
                  <Globe className="w-4 h-4 text-[#264839]" />{item.website_domain}
                </div>
                {item.is_unclaimed && (
                  <div className="text-xs text-[#7D8C85] mt-0.5" data-testid="free-website-claim-note">Claim this listing to make your website clickable.</div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 w-full md:w-auto md:max-w-xs">
            {item.is_unclaimed && (
              <button type="button" onClick={onClaim} className="text-xs uppercase tracking-[0.15em] px-5 py-3 bg-[#B35C4A] text-white hover:bg-[#994A3B] transition-colors" data-testid="claim-listing-btn">
                Is this your company? Claim this listing
              </button>
            )}
            <p className="text-xs text-[#7D8C85] leading-relaxed md:text-right" data-testid="free-contact-nudge">
              Want to contact this supplier? Use the <span className="text-[#264839] font-medium">Send Enquiry</span> form below, or{" "}
              <Link to={item.category ? categoryPath(item.category) : "/search"} className="text-[#264839] underline underline-offset-2 hover:text-[#11211A]" data-testid="free-contact-nudge-link">
                see Founding Suppliers{item.category ? ` in ${item.category.name}` : ""}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
        <div>
          <div className="tracking-overline mb-2">About</div>
          {item.description?.trim() ? (
            <p className="text-[#4A5550] leading-relaxed whitespace-pre-wrap" data-testid="free-description">{item.description}</p>
          ) : (
            <p className="text-[#9AA6A0] italic" data-testid="free-description-placeholder">
              Description coming soon.{item.is_unclaimed ? " If this is your company, claim the listing to add one." : ""}
            </p>
          )}
          {isOwner ? (
            <div className="mt-8 bg-[#F0EFEA] p-5 text-sm text-[#4A5550]" data-testid="free-listing-notice">
              Hoteliers can send you enquiries from this page — we forward them to your email. Upgrade to Founding Supplier to be contacted directly and appear first in search.
            </div>
          ) : (
            <FreeEnquiryForm listing={item} company={company} />
          )}
        </div>
        <div>
          {item.is_unclaimed && <UpgradePrompt variant="public" testId="free-upgrade-prompt-public" />}
          {!item.is_unclaimed && isOwner && <UpgradePrompt variant="owner" testId="free-upgrade-prompt-owner" />}
        </div>
      </div>
    </div>
  );
}
